import React, { createContext, useContext } from 'react';
import type { CatalogSnapshot } from 'pricing/engine';
import type { RepriceDiff } from 'pricing/repriceDiff';

/** A reprice candidate awaiting the exec's accept/cancel. Declared here rather
 *  than in the provider so the published contract carries the real shape:
 *  Phase 6 mounts RepriceDialog against `diff` and hands `candidate` straight
 *  back to acceptReprice, and an `unknown` here would push a cast onto every
 *  one of those call sites. shared/ may import the entity folders — see
 *  shared/state/types.ts, which imports domain/index the same way. */
export type RepriceState = { candidate: CatalogSnapshot; diff: RepriceDiff };

export type QuoteActionsValue = {
  saveQuote: (description: string) => Promise<void> | void;
  handleGenerateQuotePDF: () => Promise<void> | void;
  handleGenerateSystemPDF: () => Promise<void> | void;
  handleGenerateRackSlide: () => Promise<void> | void;
  execEscapeActive: () => boolean;
  logEscape: (artifactType: string, opts?: { quoteSerial?: string | null; quoteName?: string | null }) => Promise<boolean>;
  busy: string | null;

  loadSavedConfig: (saved: Record<string, unknown>, systemType?: string, quoteRow?: Record<string, unknown> | null) => void;
  /** A fresh configuration: a base system by default, or an empty expansion. */
  resetConfig: (systemType?: 'BaseSystem' | 'Expansion') => void;
  /** Signed-in user's display name/email, for the Load dialog's User column
   *  (classic: `userEmail={userName}`, App.js:1401) and Task 3's CSV export. */
  userName: string;
  showSaveDialog: boolean; setShowSaveDialog: (v: boolean) => void;
  showQuotesModal: boolean; setShowQuotesModal: (v: boolean) => void;
  showExportDialog: boolean; setShowExportDialog: (v: boolean) => void;

  quoteErrors: Record<string, string>;
  setFieldError: (field: string, value: unknown) => void;

  /** The Phase 0b special-pricing input hook, surfaced whole for the Pricing section. */
  sp: Record<string, any>;
  accountExecutives: Array<{ name: string; email: string }>;

  startReprice: () => Promise<void>;
  acceptReprice: () => Promise<void>;
  repriceDiff: RepriceState | null;
  setRepriceDiff: (v: RepriceState | null) => void;
  qtyByPartNumber: Record<string, number>;
  execConfirmElement: React.ReactNode;
};

export const QuoteActionsCtx = createContext<QuoteActionsValue | null>(null);

export function useQuoteActionsCtx(): QuoteActionsValue {
  const v = useContext(QuoteActionsCtx);
  if (!v) throw new Error('useQuoteActionsCtx must be used inside <QuoteActionsProvider>.');
  return v;
}
